import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, Users, FileText, CheckCircle, ArrowRight, 
  ArrowLeft, Upload, Sparkles, Rocket, ChevronRight, Handshake, AlertTriangle, Percent
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const steps = [
  { id: 'benvenuto', title: 'Benvenuto', icon: Sparkles },
  { id: 'ente', title: 'Dati Ente', icon: Building2 },
  { id: 'logo', title: 'Logo', icon: Upload },
  { id: 'primi-passi', title: 'Primi Passi', icon: Users },
  { id: 'completato', title: 'Completato', icon: Rocket },
];

export default function OnboardingWizard({ onComplete }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(() => {
    const saved = localStorage.getItem('onboarding_step');
    return saved ? parseInt(saved) : 0;
  });

  // Persisti step nel localStorage
  useEffect(() => {
    localStorage.setItem('onboarding_step', currentStep.toString());
  }, [currentStep]);


  const [loading, setLoading] = useState(false);
  const [enteData, setEnteData] = useState({
    denominazione: '',
    tipo_ente: 'APS',
    codice_fiscale: '',
    partita_iva: '',
    indirizzo: '',
    citta: '',
    cap: '',
    provincia: '',
    email: '',
    telefono: '',
    sito_web: '',
    descrizione: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  
  // Convenzione state
  const [codiceConvenzione, setCodiceConvenzione] = useState('');
  const [convenzioneVerificata, setConvenzioneVerificata] = useState(null); // null = non verificato, {valid: true/false, ...}
  const [verificandoConvenzione, setVerificandoConvenzione] = useState(false);

  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleEnteChange = (e) => {
    setEnteData({ ...enteData, [e.target.name]: e.target.value });
  };

  // Verifica codice convenzione in tempo reale
  const verificaCodiceConvenzione = async (codice) => {
    if (!codice || codice.trim().length < 5) {
      setConvenzioneVerificata(null);
      return;
    }
    
    setVerificandoConvenzione(true);
    try {
      const token = localStorage.getItem('token');
      // Endpoint per verificare il codice (senza applicarlo)
      const response = await fetch(`${API_URL}/api/ente/convenzione/verifica-codice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ codice: codice.trim().toUpperCase() })
      });
      
      const data = await response.json();
      
      if (response.ok && data.valid) {
        setConvenzioneVerificata({
          valid: true,
          nome: data.nome,
          sconto: data.sconto_percentuale ? `${data.sconto_percentuale}%` : `€${data.prezzo_fisso}/anno`
        });
      } else {
        setConvenzioneVerificata({ valid: false, message: data.detail || 'Codice non valido' });
      }
    } catch (error) {
      setConvenzioneVerificata({ valid: false, message: 'Errore nella verifica' });
    } finally {
      setVerificandoConvenzione(false);
    }
  };

  // Debounce per la verifica (evita troppe chiamate API)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (codiceConvenzione) {
        verificaCodiceConvenzione(codiceConvenzione);
      } else {
        setConvenzioneVerificata(null);
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, [codiceConvenzione]);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Il logo deve essere inferiore a 2MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const saveEnteData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Save ente data
      const response = await fetch(`${API_URL}/api/enti/me`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(enteData)
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || 'Errore nel salvataggio');
      }

      // Se c'è un codice convenzione valido, applicalo
      if (codiceConvenzione && convenzioneVerificata?.valid) {
        try {
          const convResponse = await fetch(`${API_URL}/api/ente/convenzione/aderisci`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include',
            body: JSON.stringify({ codice: codiceConvenzione.trim().toUpperCase() })
          });
          
          if (convResponse.ok) {
            const convData = await convResponse.json();
            toast.success(`Convenzione applicata: ${convData.sconto_applicato || convenzioneVerificata.sconto}`);
          } else {
            // Se l'adesione fallisce, non bloccare l'onboarding
            toast.warning('Impossibile applicare la convenzione. Potrai inserirla dalle Impostazioni.');
          }
        } catch (convError) {
          console.error('Errore convenzione:', convError);
          // Non bloccare l'onboarding
        }
      }

      toast.success('Dati salvati con successo');
      return true;
    } catch (error) {
      console.error('Errore:', error);
      toast.error('Errore nel salvataggio dei dati');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (currentStep === 1) {
      // Validate only required fields: denominazione and email
      if (!enteData.denominazione || enteData.denominazione.trim() === '') {
        toast.error('Inserisci la denominazione dell\'ente');
        return;
      }
      if (!enteData.email || enteData.email.trim() === '') {
        toast.error('Inserisci l\'email dell\'ente');
        return;
      }
      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(enteData.email)) {
        toast.error('Inserisci un\'email valida');
        return;
      }
      // Save ente data here (step 1 -> 2 transition)
      const saved = await saveEnteData();
      if (!saved) return;
    }

    if (currentStep === 2) {
      // Upload logo if present (optional)
      if (logoFile) {
        setLoading(true);
        try {
          const token = localStorage.getItem('token');
          const formData = new FormData();
          formData.append('logo', logoFile);
          
          const response = await fetch(`${API_URL}/api/enti/logo`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` },
            credentials: 'include',
            body: formData
          });
          
          if (!response.ok) {
            toast.error('Errore nel caricamento del logo');
            // Don't block progression, logo is optional
          }
        } catch (error) {
          console.error('Logo upload error:', error);
          // Don't block progression
        } finally {
          setLoading(false);
        }
      }
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    // Mark onboarding as complete
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/users/onboarding-complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      localStorage.removeItem('onboarding_step');
      // Setta flag locale per evitare race condition con il check API
      localStorage.setItem('onboarding_completed', 'true');
    } catch (error) {
      console.error('Errore:', error);
    }
    
    if (onComplete) {
      onComplete();
    } else {
      // Forza reload per resettare tutti gli state dell'app
      window.location.href = '/dashboard';
    }
  };

  const handleSkipToDashboard = async () => {
    // Validate minimum required fields before skipping
    if (currentStep >= 1) {
      if (!enteData.denominazione || enteData.denominazione.trim() === '') {
        toast.error('Inserisci almeno la denominazione dell\'ente per continuare');
        return;
      }
      if (!enteData.email || enteData.email.trim() === '') {
        toast.error('Inserisci almeno l\'email dell\'ente per continuare');
        return;
      }
      // Save the minimum data before skipping
      const saved = await saveEnteData();
      if (!saved) return;
    }
    
    // Mark onboarding as complete and go to dashboard
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/users/onboarding-complete`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include'
      });
      localStorage.removeItem('onboarding_step');
      localStorage.setItem('onboarding_completed', 'true');
      toast.success('Potrai completare i dati in qualsiasi momento dalle Impostazioni');
      window.location.href = '/dashboard';
    } catch (error) {
      console.error('Errore skip:', error);
      toast.error('Errore nel salvataggio');
    }
  };

  const renderStep = () => {
    switch (steps[currentStep].id) {
      case 'benvenuto':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sparkles className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Benvenuto in Assietica Gest!
            </h2>
            <p className="text-gray-600 max-w-md mx-auto mb-4">
              Completa questa breve configurazione iniziale per personalizzare la piattaforma 
              per il tuo ente.
            </p>
            <p className="text-sm text-blue-600 max-w-md mx-auto mb-6">
              Solo <strong>Nome dell'Ente</strong> ed <strong>Email</strong> sono obbligatori. 
              Potrai completare gli altri dati in qualsiasi momento.
            </p>
            <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto text-sm">
              <div className="p-4 bg-blue-50 rounded-lg">
                <Building2 className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                <span className="text-gray-700">Dati Ente</span>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <Upload className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <span className="text-gray-700">Logo</span>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <Users className="h-6 w-6 text-purple-600 mx-auto mb-2" />
                <span className="text-gray-700">Primi Passi</span>
              </div>
            </div>
          </div>
        );

      case 'ente':
        return (
          <div className="py-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Dati del tuo Ente</h2>
            <p className="text-gray-600 mb-2">Inserisci le informazioni base della tua organizzazione</p>
            <p className="text-sm text-blue-600 mb-6">
              I campi con * sono obbligatori. Gli altri potrai completarli in seguito dalle Impostazioni.
            </p>
            
            <div className="grid gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label htmlFor="denominazione">Denominazione *</Label>
                <Input
                  id="denominazione"
                  name="denominazione"
                  value={enteData.denominazione}
                  onChange={handleEnteChange}
                  placeholder="Es: Associazione Volontari XYZ APS"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="tipo_ente">Tipo Ente</Label>
                <select
                  id="tipo_ente"
                  name="tipo_ente"
                  value={enteData.tipo_ente}
                  onChange={handleEnteChange}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                >
                  <option value="APS">APS - Associazione di Promozione Sociale</option>
                  <option value="ODV">ODV - Organizzazione di Volontariato</option>
                  <option value="ETS">ETS - Ente del Terzo Settore</option>
                  <option value="ONLUS">ONLUS</option>
                  <option value="Altro">Altro</option>
                </select>
              </div>
              
              <div>
                <Label htmlFor="codice_fiscale">Codice Fiscale</Label>
                <Input
                  id="codice_fiscale"
                  name="codice_fiscale"
                  value={enteData.codice_fiscale}
                  onChange={handleEnteChange}
                  placeholder="12345678901"
                  className="mt-1"
                />
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="indirizzo">Indirizzo Sede</Label>
                <Input
                  id="indirizzo"
                  name="indirizzo"
                  value={enteData.indirizzo}
                  onChange={handleEnteChange}
                  placeholder="Via Roma 1"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="citta">Città</Label>
                <Input
                  id="citta"
                  name="citta"
                  value={enteData.citta}
                  onChange={handleEnteChange}
                  placeholder="Milano"
                  className="mt-1"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="cap">CAP</Label>
                  <Input
                    id="cap"
                    name="cap"
                    value={enteData.cap}
                    onChange={handleEnteChange}
                    placeholder="20100"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="provincia">Prov.</Label>
                  <Input
                    id="provincia"
                    name="provincia"
                    value={enteData.provincia}
                    onChange={handleEnteChange}
                    placeholder="MI"
                    maxLength={2}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={enteData.email}
                  onChange={handleEnteChange}
                  placeholder="info@tuoente.it"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label htmlFor="telefono">Telefono</Label>
                <Input
                  id="telefono"
                  name="telefono"
                  value={enteData.telefono}
                  onChange={handleEnteChange}
                  placeholder="+39 02 1234567"
                  className="mt-1"
                />
              </div>
              
              {/* Sezione Convenzione */}
              <div className="md:col-span-2 mt-4 pt-4 border-t">
                <div className="flex items-center gap-2 mb-3">
                  <Handshake className="h-5 w-5 text-blue-600" />
                  <Label className="text-base font-medium">Hai un codice convenzione?</Label>
                </div>
                <p className="text-sm text-gray-500 mb-3">
                  Se la tua associazione fa parte di una federazione nazionale, potresti avere diritto a uno sconto. 
                  Inserisci il codice ricevuto (opzionale).
                </p>
                <div className="flex gap-3 items-start">
                  <div className="flex-1">
                    <Input
                      id="codice_convenzione"
                      value={codiceConvenzione}
                      onChange={(e) => setCodiceConvenzione(e.target.value.toUpperCase())}
                      placeholder="Es. ANPAS-2026-XK9F"
                      className="font-mono"
                      data-testid="input-codice-convenzione-onboarding"
                    />
                  </div>
                  {verificandoConvenzione && (
                    <div className="flex items-center text-gray-500">
                      <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  )}
                </div>
                
                {/* Feedback convenzione */}
                {convenzioneVerificata && (
                  <div className={`mt-3 p-3 rounded-lg flex items-start gap-2 ${
                    convenzioneVerificata.valid 
                      ? 'bg-green-50 border border-green-200' 
                      : 'bg-amber-50 border border-amber-200'
                  }`}>
                    {convenzioneVerificata.valid ? (
                      <>
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-green-800">Convenzione valida!</p>
                          <p className="text-sm text-green-700">
                            <strong>{convenzioneVerificata.nome}</strong> - Sconto: {convenzioneVerificata.sconto}
                          </p>
                        </div>
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">Codice non riconosciuto</p>
                          <p className="text-sm text-amber-700">
                            Verifica il codice o lascia vuoto per continuare senza convenzione.
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                )}
                
                {!codiceConvenzione && (
                  <p className="text-xs text-gray-400 mt-2">
                    Non hai un codice? Nessun problema, potrai inserirlo in seguito dalle Impostazioni.
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 'logo':
        return (
          <div className="py-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Carica il Logo</h2>
            <p className="text-gray-600 mb-6">
              Il logo apparirà su tutti i documenti generati (PDF, certificati, ecc.)
            </p>
            
            <div className="flex flex-col items-center">
              <div className="w-48 h-48 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center mb-4 bg-gray-50 overflow-hidden">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo preview" className="max-w-full max-h-full object-contain" />
                ) : (
                  <div className="text-center p-4">
                    <Upload className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                    <span className="text-sm text-gray-500">Nessun logo</span>
                  </div>
                )}
              </div>
              
              <input
                type="file"
                id="logo-upload"
                accept="image/*"
                onChange={handleLogoChange}
                className="hidden"
              />
              <label htmlFor="logo-upload">
                <Button variant="outline" asChild>
                  <span className="cursor-pointer">
                    <Upload className="h-4 w-4 mr-2" />
                    {logoPreview ? 'Cambia Logo' : 'Carica Logo'}
                  </span>
                </Button>
              </label>
              
              <p className="text-xs text-gray-500 mt-4">
                Formati supportati: PNG, JPG, SVG. Max 2MB.
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Puoi saltare questo passaggio e caricarlo dopo dalle Impostazioni.
              </p>
            </div>
          </div>
        );

      case 'primi-passi':
        return (
          <div className="py-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Primi Passi</h2>
            <p className="text-gray-600 mb-6">
              Ecco cosa puoi fare subito per iniziare a usare Assietica Gest
            </p>
            
            <div className="space-y-4">
              <Card className="hover:bg-blue-50 transition-colors cursor-pointer" onClick={() => navigate('/volontari')}>
                <CardContent className="flex items-center gap-4 py-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Users className="h-6 w-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium">Aggiungi i tuoi Volontari</h3>
                    <p className="text-sm text-gray-500">Inizia a creare l'anagrafica dei volontari</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </CardContent>
              </Card>
              
              <Card className="hover:bg-green-50 transition-colors cursor-pointer" onClick={() => navigate('/soci')}>
                <CardContent className="flex items-center gap-4 py-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                    <Users className="h-6 w-6 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium">Gestisci i Soci</h3>
                    <p className="text-sm text-gray-500">Registra i soci e le quote associative</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </CardContent>
              </Card>
              
              <Card className="hover:bg-purple-50 transition-colors cursor-pointer" onClick={() => navigate('/eventi/nuovo')}>
                <CardContent className="flex items-center gap-4 py-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-6 w-6 text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium">Crea il primo Evento</h3>
                    <p className="text-sm text-gray-500">Pianifica attività e gestisci le presenze</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </CardContent>
              </Card>
              
              <Card className="hover:bg-amber-50 transition-colors cursor-pointer" onClick={() => navigate('/kit-amministrativo')}>
                <CardContent className="flex items-center gap-4 py-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                    <FileText className="h-6 w-6 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium">Scarica i Moduli Base</h3>
                    <p className="text-sm text-gray-500">Kit amministrativo con tutti i documenti necessari</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </CardContent>
              </Card>
            </div>
          </div>
        );

      case 'completato':
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-10 w-10 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Configurazione Completata!
            </h2>
            <p className="text-gray-600 max-w-md mx-auto mb-6">
              Ottimo lavoro! Il tuo ente è pronto per essere gestito con Assietica Gest.
              Puoi sempre modificare le impostazioni dalla sezione dedicata.
            </p>
            
            <div className="bg-blue-50 rounded-lg p-4 max-w-md mx-auto mb-6">
              <p className="text-sm text-blue-800">
                <strong>Suggerimento:</strong> Consulta il <button onClick={() => navigate('/manuale')} className="underline">Manuale d'Uso</button> per 
                scoprire tutte le funzionalità disponibili.
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            {steps.map((step, index) => (
              <div 
                key={step.id}
                className={`flex items-center gap-2 ${
                  index <= currentStep ? 'text-blue-600' : 'text-gray-400'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  index < currentStep 
                    ? 'bg-blue-600 text-white' 
                    : index === currentStep 
                      ? 'bg-blue-100 text-blue-600 border-2 border-blue-600' 
                      : 'bg-gray-100 text-gray-400'
                }`}>
                  {index < currentStep ? <CheckCircle className="h-4 w-4" /> : index + 1}
                </div>
                <span className="hidden sm:inline text-sm">{step.title}</span>
              </div>
            ))}
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Content Card */}
        <Card className="shadow-lg">
          <CardContent className="p-6 md:p-8">
            {renderStep()}
            
            {/* Navigation */}
            <div className="flex justify-between mt-8 pt-6 border-t">
              {currentStep > 0 && currentStep < steps.length - 1 ? (
                <Button variant="outline" onClick={handlePrev}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Indietro
                </Button>
              ) : (
                <div />
              )}
              
              {currentStep < steps.length - 1 ? (
                <Button onClick={handleNext} disabled={loading}>
                  {loading ? 'Salvataggio...' : currentStep === 0 ? 'Inizia' : 'Continua'}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleComplete} className="bg-green-600 hover:bg-green-700">
                  <Rocket className="h-4 w-4 mr-2" />
                  Vai alla Dashboard
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Skip Option - Bottone ben visibile */}
        {currentStep > 0 && currentStep < steps.length - 1 && (
          <div className="text-center mt-6">
            <Button 
              variant="outline"
              onClick={handleSkipToDashboard}
              disabled={loading}
              className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100 hover:border-slate-400 px-6 py-3 text-sm font-medium"
              data-testid="skip-onboarding-btn"
            >
              <ChevronRight className="h-4 w-4 mr-2" />
              Vai alla Dashboard — completero' il processo in un secondo momento
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
